import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import Select from 'react-select';

import { UserTaskActions } from "redux/actions/UserTaskActions";
import { MachineActions } from "redux/actions/settings/MachineActions";
import { MachineMaintenanceActions } from "redux/actions/settings/MachineMaintenanceActions";
import { UserActions } from "redux/actions/settings/UserActions";
import { ModalActions } from "redux/actions/ModalActions";
import { TableColumn, Table, TableRow, sortType } from "app/components/common/Table";
import { PageContentHeader } from "app/components/common/PageContentHeader";
import Translator from "app/components/common/Translator";
import { Translate } from "infrastructure/mapping/Translate";

import { StoreModel } from "models/StoreModel";
import { LoginUser } from "models/LoginUser";
import { Translations } from "models/Translations";
import { UserTask } from "models/UserTask";
import { Machine } from "models/Machine";
import { MachineMaintenance } from "models/MachineMaintenance";
import { User } from "models/User";
import { UserRoleTypes } from "models/UserRole";

interface Props {
    fetchUserTasks: typeof UserTaskActions.fetchUserTasks;
    addUserTask: typeof UserTaskActions.addUserTask;
    addUserTaskForm: typeof ModalActions.showModal;
    fetchMachines: typeof MachineActions.fetchMachines;
    fetchMachineMaintenances: typeof MachineMaintenanceActions.fetchMachineMaintenances;
    fetchAllUserData: typeof UserActions.fetchAllUserData;
    loginUser: LoginUser;
    userTasks: UserTask[];
    machines: Machine[];
    machineMaintenances: MachineMaintenance[];
    users: User[];
    translations: Translations;
}

type Filter = 'assigned' | 'created';

interface State {
    filter: Filter;
}

class MyTasks extends React.Component<Props, State> {
    public readonly state: State = {
        filter: 'assigned'
    }

    componentDidMount() {
        if (this.loggedIn()) {
            if (!this.props.userTasks) {
                this.props.fetchUserTasks(this.props.loginUser.token);
            }
            if (!this.props.machines) {
                this.props.fetchMachines(this.props.loginUser.token);
            }
            if (!this.props.machineMaintenances) {
                this.props.fetchMachineMaintenances(this.props.loginUser.token);
            }
        }
        this.loadUsers();
    }

    componentDidUpdate() {
        this.loadUsers();
    }

    usersRequested = false;

    // the user list is PoolManager only, operators assign tasks to themselves
    // after a refresh the roles arrive after mount
    loadUsers() {
        if (this.loggedIn() && this.isPoolManager() && !this.props.users && !this.usersRequested) {
            this.usersRequested = true;
            this.props.fetchAllUserData(this.props.loginUser.token);
        }
    }

    isPoolManager() {
        return !!this.props.loginUser.userData?.roles.find(r => r.name === UserRoleTypes.poolManager);
    }

    me(): User {
        return this.props.loginUser.userData!;
    }

    // operators cannot load the user list, their tasks are always their own
    newTaskForm() {
        const machine = Machine.assignedTo(this.props.machines, this.me().id) ?? Machine.empty();
        return this.isPoolManager()
            ? { machine, userToDo: this.me(), dateTodo: new Date(), machineMaintenance: MachineMaintenance.empty() }
            : { machine, dateTodo: new Date(), machineMaintenance: MachineMaintenance.empty() };
    }

    addTask(token: string, task: UserTask) {
        this.props.addUserTask(token, this.isPoolManager() ? task : { ...task, userToDo: User.toRef(this.me()) });
    }

    loggedIn() {
        return this.props.loginUser && this.props.loginUser.token;
    }

    ready() {
        return this.loggedIn() && this.props.loginUser.userData?.email && this.props.userTasks && this.props.machines && this.props.machineMaintenances && (!this.isPoolManager() || this.props.users);
    }

    filterOptions() {
        return [
            { label: Translate.text(this.props.translations, "Assigned to me"), value: 'assigned' as Filter },
            { label: Translate.text(this.props.translations, "Created by me"), value: 'created' as Filter },
        ];
    }

    isMine(t: UserTask) {
        const user = this.state.filter === 'assigned' ? t.userToDo : t.userCreated;
        return user?.email === this.props.loginUser.email;
    }

    setTaskData(): TableRow[] {
        return this.props.userTasks
            .filter(t => UserTask.isOpen(t) && this.isMine(t))
            .map(t => ({
                dataObject: {
                    id: t.id,
                    machine: t.machine,
                    task: UserTask.description(t),
                    dateTodo: t.dateTodo,
                    userToDo: t.userToDo,
                    userCreated: t.userCreated,
                    archivedDate: t.archivedDate
                },
                cells: [
                    { name: "dateTodo", sortVal: new Date(t.dateTodo).getTime(), cell: <>{new Date(t.dateTodo).toLocaleDateString()}</> },
                    { name: "userToDo", sortVal: t.userToDo?.userName ?? '', cell: <>{t.userToDo?.userName}</> },
                    { name: "userCreated", sortVal: t.userCreated?.userName ?? '', cell: <>{t.userCreated?.userName}</> },
                ]
            }));
    }

    setColumns(): TableColumn[] {
        return [
            { header: 'Id', accessor: 'id' },
            { header: 'Machine', accessor: 'machine' },
            { header: 'Task', accessor: 'task' },
            { header: 'Date To Do', accessor: 'dateTodo' },
            { header: 'To Do by', accessor: 'userToDo' },
            { header: 'Created by', accessor: 'userCreated' },
        ];
    }

    render() {
        if (!this.ready()) {
            return (
                <div className="c-page__content">
                    <PageContentHeader text="My Tasks" />
                    <Translator text="Loading" />
                </div>
            )
        }
        const options = this.filterOptions();
        return (
            <div className="c-page__content">
                <PageContentHeader text="My Tasks" />
                <div className="c-card">
                    <Select
                        value={options.find(o => o.value === this.state.filter)}
                        onChange={o => o && this.setState({ filter: o.value })}
                        options={options}
                        className={"c-form__select"}
                        classNamePrefix="c-select"
                        // render the menu outside the card, which would clip it
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 10 }) }}
                    />
                </div>
                <div className="c-card">
                    <Table
                        editable={false}
                        {...(this.state.filter === 'created' && {
                            addAction: this.addTask.bind(this),
                            addFormAction: this.props.addUserTaskForm,
                            addFormData: { header: "Add a new Task", formObject: this.newTaskForm() },
                            selectConfigs: [
                                { divId: 'userToDo', storeKey: 'users', label: (u: object) => (u as User).userName },
                                // only the maintenances planned for the selected machine's type, hidden until a machine is picked
                                {
                                    divId: 'machineMaintenance',
                                    visible: (fo: object) => !!(fo as UserTask).machine?.id,
                                    label: (mm: object) => (mm as MachineMaintenance).maintenance.name,
                                    filter: (mm: object, fo: object) => (mm as MachineMaintenance).machineType.id === (fo as UserTask).machine?.machineType?.id
                                },
                            ],
                            optionals: ['machineMaintenance'],
                        })}
                        columns={this.setColumns()}
                        defaultSort={{ columnId: 'dateTodo', sortType: sortType.asc }}
                        tableData={this.setTaskData()}
                        translations={this.props.translations}
                        loginUser={this.props.loginUser}
                    />
                </div>
            </div>
        );
    }
}

function mapStateToProps(state: StoreModel) {
    return {
        loginUser: state.loginUser,
        userTasks: state.userTasks,
        machines: state.machines,
        machineMaintenances: state.machineMaintenances,
        users: state.users,
        translations: state.translations
    };
}
function mapDispatchToProps(dispatch: Dispatch) {
    return {
        fetchUserTasks: bindActionCreators(UserTaskActions.fetchUserTasks, dispatch),
        addUserTask: bindActionCreators(UserTaskActions.addUserTask, dispatch),
        addUserTaskForm: bindActionCreators(ModalActions.showModal, dispatch),
        fetchMachines: bindActionCreators(MachineActions.fetchMachines, dispatch),
        fetchMachineMaintenances: bindActionCreators(MachineMaintenanceActions.fetchMachineMaintenances, dispatch),
        fetchAllUserData: bindActionCreators(UserActions.fetchAllUserData, dispatch),
    };
}
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(MyTasks);
